import React, { useState, useEffect, useMemo } from 'react'
import {
  ButtonElement,
  Search,
  TableHeaders,
  useAngularServices,
} from '@/react/components'
import closeIcon from '@/assets/icons/close-icon.svg'
import { css } from '@emotion/react'
import { useRef } from 'react'
import { CurrentUserType } from '@/react/types/documentation'
import { TrainingsCheckmark } from '@/react/componentAssets/TrainingsCheckmark'
import { singleHeaderTypes } from '@/react/screens/components/types'
import AssignInternalRow from './AssignInternalRow'
import { User, InternalAssignee } from '@/react/types/observations'
import sortArrowsActiveFirst from '@/assets/icons/sorting-icons/sort-arrow-up.svg'
import sortArrowsActiveSecond from '@/assets/icons/sorting-icons/sort-arrow-down.svg'
import { useInternalAssigneeAPI } from '@/react/hooks/internalExternalSection/useInternalAssigneeAPI'
import { useUsersList } from '@/react/hooks/internalExternalSection/useUsersList'

interface ConfirmationReminderModalProps {
  title: string
  handleClose: () => void
  answerId: number
  observationId?: number
  questionId?: number
  existingAssignees?: InternalAssignee[]
  onAssignmentComplete?: () => void
}

const AssignInternalModal = ({
  title = '',
  handleClose,
  answerId,
  observationId,
  questionId,
  existingAssignees,
  onAssignmentComplete,
}: ConfirmationReminderModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const { CurrentUser } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color
  const [isButtonDisabled, setButtonDisabled] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [order, setOrder] = useState('')

  const currentUserId = useMemo(() => {
    const id = CurrentUser.getId()
    return typeof id === 'string' ? parseInt(id, 10) : id
  }, [CurrentUser])

  const { processInternalAssignments } = useInternalAssigneeAPI({
    answerId,
    observationId,
    questionId,
    existingAssignees,
  })

  const { users, loading, loadingMore, hasMore, loadMoreUsers, filteredUsers } =
    useUsersList({
      search,
      order,
      currentUserId,
    })

  // Initialize selected users with existing assignees
  React.useEffect(() => {
    if (existingAssignees && existingAssignees.length > 0) {
      const existingUserIds = existingAssignees
        .filter((assignee) => !assignee.deleted)
        .map((assignee) => assignee.user)
      setSelectedUsers(existingUserIds)
    }
  }, [existingAssignees])

  // Update checked state based on selection - memoized to prevent unnecessary recalculations
  const allSelected = useMemo(
    () =>
      filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length,
    [filteredUsers.length, selectedUsers.length],
  )

  const someSelected = useMemo(
    () =>
      selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length,
    [selectedUsers.length, filteredUsers.length],
  )

  const handleOutsideClick = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose()
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  const handleSelect = (userId: number) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId)
      } else {
        return [...prev, userId]
      }
    })
  }

  const TABLE_HEADER_SETTINGS: singleHeaderTypes[] = [
    {
      className: 'table__header',
      name: '',
      type: 'is_active',
      additionalStyles: css({
        width: '53px',
        minWidth: '53px',
        textAlign: 'center',
        padding: '0px 0px 8px 0px',
        height: '22px',
        p: {
          width: '0px',
        },
        '.header-base': {
          width: '100%',
          margin: 0,
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }),
      additionalInnerElement: (
        <div className="header-actions">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(input) => {
                if (input) input.indeterminate = someSelected
              }}
              onChange={() => {
                if (allSelected) {
                  setSelectedUsers([])
                } else {
                  const allUserIds = users.map((user) => user.id)
                  setSelectedUsers(allUserIds)
                }
              }}
            />
            <span className="checkmark">
              <TrainingsCheckmark color={variableColor} />
            </span>
          </label>

          <img
            src={
              order === 'is_active'
                ? sortArrowsActiveSecond
                : sortArrowsActiveFirst
            }
            className="sort-icon"
            alt="sort icon"
            onClick={() => {
              setOrder(order === 'is_active' ? '-is_active' : 'is_active')
            }}
          />
        </div>
      ),
    },
    {
      className: 'table__header',
      name: 'Name',
      type: 'name',
      additionalStyles: css({
        width: '260px',
        minWidth: '260px',
        textAlign: 'left',
        paddingLeft: 0,
        height: '30px',
        p: {
          color: '#656565',
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '-0.12px',
        },
        '.header-base': {
          width: 'fit-content',
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
        },
      }),
      additionalInnerElement: (
        <img
          src={
            order === 'name' ? sortArrowsActiveSecond : sortArrowsActiveFirst
          }
          className="sort-icon"
          alt="sort icon"
          onClick={() => {
            setOrder(order === 'name' ? '-name' : 'name')
          }}
        />
      ),
    },
    {
      className: 'table__header',
      name: 'ID',
      type: 'employee_id',
      additionalStyles: css({
        width: '100px',
        minWidth: '100px',
        textAlign: 'left',
        paddingLeft: 0,
        height: '30px',
        p: {
          color: '#656565',
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '-0.12px',
        },
        '.header-base': {
          width: 'fit-content',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',

          img: {
            cursor: 'pointer',
          },
        },
      }),
      additionalInnerElement: (
        <img
          src={
            order === 'employee_id'
              ? sortArrowsActiveSecond
              : sortArrowsActiveFirst
          }
          alt="sort icon"
          onClick={() => {
            setOrder(order === 'employee_id' ? '-employee_id' : 'employee_id')
          }}
        />
      ),
    },
  ]

  const handleSubmit = async () => {
    setButtonDisabled(true)

    try {
      const result = await processInternalAssignments(selectedUsers)

      if (result.errors.length > 0) {
        console.warn('Some assignments had issues:', result.errors.join(', '))
      }

      if (result.success && onAssignmentComplete) {
        onAssignmentComplete()
      }

      if (result.success) {
        handleClose()
      } else {
        console.error('Assignment failed:', result.errors.join(', '))
        setButtonDisabled(false)
      }
    } catch (error) {
      console.error('Error in assignment process:', error)
      setButtonDisabled(false)
    }
  }

  // Memoize scroll handler for better performance
  useEffect(() => {
    const handleScroll = () => {
      const container = tableContainerRef.current
      if (!container || loadingMore || !hasMore) return

      const { scrollTop, scrollHeight, clientHeight } = container
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreUsers()
      }
    }

    const container = tableContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [loadingMore, hasMore, loadMoreUsers])

  return (
    <div
      css={assignInternalModalStyles(variableColor)}
      className="modal-container"
      ref={modalRef}
      onMouseOver={(e) => {
        e.stopPropagation()
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <img
        src={closeIcon}
        alt="cross-icon"
        className="close-icon"
        onClick={() => {
          handleClose()
        }}
      />
      <div className="modal-content">
        <p className="message-name">{title}</p>
        <div className="search-container">
          <Search
            search={search}
            searchCallback={setSearch}
            pageCallback={() => {
              // empty function
            }}
            updateUrl={false}
            onClear={() => {
              setSelectedUsers([])
            }}
            width="264px"
          />
        </div>
        <div className="table-container" ref={tableContainerRef}>
          {loading ? (
            <div className="loader-container">
              <div className="spinner"></div>
              <p className="loading-text">Loading users...</p>
            </div>
          ) : (
            <table>
              <TableHeaders
                headers={TABLE_HEADER_SETTINGS}
                callback={setOrder}
                popUpLocation={false}
                updateUrl={false}
                order={order}
                setOrder={setOrder}
              />
              <tbody>
                {filteredUsers.map((userData: User, index: number) => (
                  <AssignInternalRow
                    key={userData.id}
                    index={index}
                    checked={selectedUsers.includes(userData.id)}
                    user={userData}
                    handleSelect={() => handleSelect(userData.id)}
                    variableColor={variableColor}
                    search={search}
                  />
                ))}
                {loadingMore && (
                  <tr>
                    <td colSpan={3} className="loading-more-row">
                      <div className="loading-more">
                        <div className="spinner-small"></div>
                        <span>Loading more users...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <ButtonElement
          text="OK"
          buttonType="submit"
          functionToTrigger={() => {
            void handleSubmit()
          }}
          disabled={isButtonDisabled}
        />
      </div>
    </div>
  )
}

const assignInternalModalStyles = (variableColor: string) =>
  css({
    position: 'absolute',
    backgroundColor: 'white',
    boxShadow: '0px 7px 29px 0px rgba(0, 0, 0, 0.18)',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10000,
    isolation: 'isolate',
    maxWidth: '500px',
    width: '100%',
    '.modal-content': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px',
    },
    '.close-icon': {
      position: 'absolute',
      top: 10,
      right: 10,
      width: '10px',
      height: '10px',
      cursor: 'pointer',
      zIndex: 10,
    },
    '.message-name': {
      textAlign: 'center',
      fontSize: '14px',
      color: '#656565',
      fontWeight: 700,
      margin: 0,
      marginBottom: '16px',
    },
    '.checkbox-container': {
      display: 'inline-block',
      position: 'relative',
      cursor: 'pointer',
      paddingLeft: '15px',
      margin: 0,
    },
    '.checkmark': {
      height: '22px',
      width: '22px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid #E6E6E6',
      background: '#fff',
      svg: {
        display: 'none',
      },
    },
    '.checkmark::after': {
      display: 'none !important',
    },
    'input:checked ~ .checkmark': {
      img: {
        height: '22px',
        width: '22px',
        display: 'block',
        background: 'white',
      },
      svg: {
        height: '22px',
        width: '22px',
        display: 'block',
        background: 'white',
      },
    },
    '.delete-subtitle': {
      color: '#666',
      textAlign: 'center',
      fontSize: '14px',
      margin: 0,
      marginBottom: '25px',
      width: '206px',
    },
    '.search-container': {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      marginBottom: '16px',
    },
    '.table-container': {
      width: '100%',
      height: '210px',
      overflowY: 'auto',
      marginBottom: '16px',
    },

    '.loader-container': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      minHeight: '200px',
    },
    '.spinner': {
      border: '3px solid #f3f3f3',
      borderTop: `3px solid ${variableColor}`,
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
      marginBottom: '12px',
    },
    '.spinner-small': {
      border: '2px solid #f3f3f3',
      borderTop: `2px solid ${variableColor}`,
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      animation: 'spin 1s linear infinite',
      marginRight: '8px',
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
    '.loading-text': {
      color: '#656565',
      fontSize: '14px',
      fontWeight: 500,
      margin: 0,
    },
    '.checkbox-td': {
      padding: '0px 10px 0px 8px',
    },
    '.dark': {
      background: '#F5F6F7',
      borderBottom: '1px solid #E7EAEC',
      borderTop: '1px solid #E7EAEC',
    },
    '.cell': {
      margin: 0,
    },
    td: {
      height: '30px',
    },
    '.assign-internal-row': {
      '&:nth-child(last-child)': {
        borderBottom: '1px solid #E7EAEC',
      },
    },
    '.sort-icon': {
      cursor: 'pointer',
      transition: 'opacity 0.2s ease',
      '&:hover': {
        opacity: 0.7,
      },
    },
    '.header-actions': {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      position: 'relative',
      paddingLeft: '8px',

      img: {
        cursor: 'pointer',
        position: 'absolute',
        right: '8px',
        top: '70%',
        transform: 'translateY(-50%)',
      },
    },
    '.loading-more-row': {
      textAlign: 'center',
      padding: '16px',
    },
    '.loading-more': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px',
      fontSize: '12px',
      color: '#656565',
      fontWeight: 500,
    },
  })

export default AssignInternalModal
